import {
	FrontendBatchType,
	FrontendCompanySampleMatrix,
	FrontendCompanyTestCategory,
	FrontendCompanyTestHierarchyNode,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleInstance,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendUserInfo,
} from "node-library/dist/portal";
import { useState } from "react";
import BatchGenerationInstanceCard from "./batchGenerationInstanceCard";
import { Severity } from "node-library/dist/constants";
import { useTranslation } from "react-i18next";
import { Divider } from "@mui/material";
import { useSnackbar } from "../../../../common-components/toast/showToastMessage";
import { YellowWarnIcon } from "../../../../common-components/toast/warnIcons";
import CreateOrUpdateSpecificationReadingDrawer from "../../../../common-components/drawers/createOrUpdateSpecificationReadingDrawer";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	user: FrontendUserInfo;

	testCategories: Array<FrontendCompanyTestCategory>;
	selectedBatchType: FrontendBatchType;
	instancesToBatch: Array<FrontendSampleInstance>;
	setInstancesToBatch: React.Dispatch<
		React.SetStateAction<Array<FrontendSampleInstance>>
	>;
	allInstances: Array<FrontendSampleInstance>;
	instanceToVerify: FrontendSampleInstance | undefined;
	setInstanceToVerify: React.Dispatch<
		React.SetStateAction<FrontendSampleInstance | undefined>
	>;
	requireDuplicateInstance: boolean;
	setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
	specDirectories: Array<FrontendSampleSpecificationDirectory>;
	updateSpecification: (spec: FrontendSampleSpecification) => void;
}

export default function BatchGenerationInstanceContainer(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();
	const {
		company,
		setCompany,
		user,
		testCategories,
		selectedBatchType,
		instancesToBatch,
		setInstancesToBatch,
		allInstances,
		instanceToVerify,
		setInstanceToVerify,
		requireDuplicateInstance,
		specDirectories,
		updateSpecification,
	} = props;

	const [draggingInstance, setDraggingInstance] = useState<
		FrontendSampleInstance | undefined
	>();

	const [readingDrawerOpen, setReadingDrawerOpen] = useState<boolean>(false);
	const [selectedSpecification, setSelectedSpecification] = useState<
		FrontendSampleSpecification | undefined
	>();

	const specFileTestCategory: FrontendCompanyTestCategory | undefined =
		selectedSpecification
			? testCategories.find(
					(t) => t.testCategoryId === selectedSpecification.testCategoryId,
				)
			: undefined;
	function openSpecReadingDrawer(spec: FrontendSampleSpecification) {
		setSelectedSpecification(spec);
		setReadingDrawerOpen(true);
	}

	const instanceIdsIncluded: Array<string> = instancesToBatch.map(
		(instance) => {
			return instance.sampleInstanceId;
		},
	);
	const instancesNotIncluded: Array<FrontendSampleInstance> =
		allInstances.filter((instance) => {
			return !instanceIdsIncluded.includes(instance.sampleInstanceId);
		});
	const hierarchyNodeParentMapList: Map<
		string,
		Map<string, FrontendCompanyTestHierarchyNode[]>
	> = new Map();
	for (const testCategoryId of selectedBatchType.testCategoryIds) {
		const matchingCategory: FrontendCompanyTestCategory = testCategories.find(
			(category) => {
				return category.testCategoryId === testCategoryId;
			},
		) as FrontendCompanyTestCategory;
		const hierarchyNodeParentMap = new Map<
			string,
			FrontendCompanyTestHierarchyNode[]
		>();

		matchingCategory.hierarchyNodes.forEach((node) => {
			const parentId = node.parentNodeId || "root";
			if (!hierarchyNodeParentMap.has(parentId))
				hierarchyNodeParentMap.set(parentId, [node]);
			else {
				hierarchyNodeParentMap.get(parentId)!.push(node);
			}
		});

		hierarchyNodeParentMapList.set(testCategoryId, hierarchyNodeParentMap);
	}

	function onDragStart(e: React.DragEvent, instance: FrontendSampleInstance) {
		e.dataTransfer.effectAllowed = "move";
		setDraggingInstance(instance);
	}

	function onDropToBatchList(e: React.DragEvent) {
		e.preventDefault();
		if (!draggingInstance) return;

		// Move from unbatched to batched list
		if (instancesToBatch.length >= 20) {
			//do not add more than 20 in a batch
			openSnackbar(
				translation("LimitReached"),
				Severity.WARN,
				translation("BatchInstanceLimitReachedDescription"),
				<YellowWarnIcon />,
			);
			return;
		}
		if (
			!instancesToBatch.find(
				(inst) => inst.sampleInstanceId === draggingInstance.sampleInstanceId,
			)
		) {
			setInstancesToBatch([...instancesToBatch, draggingInstance]);

			if (!instanceToVerify && requireDuplicateInstance) {
				setInstanceToVerify(draggingInstance);
			}
		}
		setDraggingInstance(undefined);
	}

	function onDropToUnbatchedList(e: React.DragEvent) {
		e.preventDefault();
		if (!draggingInstance) return;

		// Move from batched to unbatched list
		if (
			!instancesNotIncluded.find(
				(inst) => inst.sampleInstanceId === draggingInstance.sampleInstanceId,
			)
		) {
			const filteredBatch = instancesToBatch.filter(
				(inst) => inst.sampleInstanceId !== draggingInstance.sampleInstanceId,
			);
			setInstancesToBatch(filteredBatch);
			if (
				instanceToVerify &&
				instanceToVerify.sampleInstanceId ===
					draggingInstance.sampleInstanceId &&
				requireDuplicateInstance
			) {
				//the instance to verify is removed, change to the first instance in the batch
				setInstanceToVerify(
					filteredBatch.length == 0 ? undefined : filteredBatch[0],
				);
			}
		}
		setDraggingInstance(undefined);
	}

	function onDragOver(e: React.DragEvent) {
		e.preventDefault(); // Needed to allow drop
	}

	const unbatchedInstancesSection = (
		<div
			className="batch-generation-instance-drag-component"
			onDrop={onDropToUnbatchedList}
			onDragOver={onDragOver}
			style={{ border: "1px dashed gray", padding: "0px 10px 10px 10px" }}
		>
			<div className="typography-heading-03 typography-bold batch-generation-drag-component-sticky-header">{`${translation(
				"AvailableInstances",
			)} (${instancesNotIncluded.length} ${translation("Instances")})`}</div>
			<div className="batch-generation-instance-card-container">
				{instancesNotIncluded.map((instance) => {
					const instanceCategoryId: string = instance.sample.testCategoryId;
					const hierarchyNodeParentMap = hierarchyNodeParentMapList.get(
						instanceCategoryId,
					) as Map<string, FrontendCompanyTestHierarchyNode[]>;
					const selectedTestCategory = testCategories.find((category) => {
						return category.testCategoryId === instanceCategoryId;
					}) as FrontendCompanyTestCategory;
					return (
						<BatchGenerationInstanceCard
							company={company}
							key={instance.sampleInstanceId}
							selectedTestCategory={selectedTestCategory}
							hierarchyNodeParentMap={hierarchyNodeParentMap}
							instance={instance}
							isSelectedAsVerifySample={false}
							isBatched={false}
							batchType={selectedBatchType}
							onDragStart={onDragStart}
							selectAsInstanceToVerify={() => {}}
							requireDuplicateInstance={requireDuplicateInstance}
							openSpecReadingDrawer={() => {
								if (instance.specification) {
									openSpecReadingDrawer(instance.specification);
								}
							}}
						/>
					);
				})}
			</div>
		</div>
	);

	const batchedInstancesSection = (
		<div
			className="batch-generation-instance-drag-component"
			onDrop={onDropToBatchList}
			onDragOver={onDragOver}
			style={{ border: "1px dashed green", padding: "0px 10px 10px 10px" }}
		>
			<div className="typography-heading-03 typography-bold batch-generation-drag-component-sticky-header">{`${translation(
				"InstancesToBatch",
			)} (${instancesToBatch.length}/20) ${translation("Instances")}`}</div>
			<div className="batch-generation-instance-card-container">
				{instancesToBatch.map((instance) => {
					const instanceCategoryId: string = instance.sample.testCategoryId;
					const hierarchyNodeParentMap = hierarchyNodeParentMapList.get(
						instanceCategoryId,
					) as Map<string, FrontendCompanyTestHierarchyNode[]>;
					const selectedTestCategory = testCategories.find((category) => {
						return category.testCategoryId === instanceCategoryId;
					}) as FrontendCompanyTestCategory;
					return (
						<BatchGenerationInstanceCard
							company={company}
							key={instance.sampleInstanceId}
							selectedTestCategory={selectedTestCategory}
							hierarchyNodeParentMap={hierarchyNodeParentMap}
							instance={instance}
							batchType={selectedBatchType}
							isBatched={true}
							isSelectedAsVerifySample={
								instanceToVerify
									? instanceToVerify.sampleInstanceId ===
										instance.sampleInstanceId
									: false
							}
							onDragStart={onDragStart}
							selectAsInstanceToVerify={() => {
								//disabled if does not require verify instance
								if (requireDuplicateInstance) {
									setInstanceToVerify(instance);
								}
							}}
							requireDuplicateInstance={requireDuplicateInstance}
							openSpecReadingDrawer={() => {
								if (instance.specification) {
									openSpecReadingDrawer(instance.specification);
								}
							}}
						/>
					);
				})}
			</div>
		</div>
	);
	return (
		<div className="batch-generation-instance-drag-container">
			{batchedInstancesSection}
			{unbatchedInstancesSection}
			{readingDrawerOpen && selectedSpecification && specFileTestCategory && (
				<CreateOrUpdateSpecificationReadingDrawer
					company={company}
					setCompany={setCompany}
					testCategory={specFileTestCategory}
					// Derive matrix from the sample associated with the spec
					selectedMatrix={
						specFileTestCategory.sampleMatrices.find(
							(m) => m.matrixId === selectedSpecification.matrixId,
						) as FrontendCompanySampleMatrix
					}
					selectedSpecFile={selectedSpecification}
					user={user}
					open={readingDrawerOpen}
					setOpen={setReadingDrawerOpen}
					specDirectories={specDirectories}
					updateSpecification={updateSpecification}
				/>
			)}
		</div>
	);
}
